import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import Particle from "../../Components/Particle.js";
import { END_POINT_RESET_PASSWORD } from "../../Config/index.js";
import { useAuth } from "../../Context/AuthContext";

const inputClass = "w-full px-4 py-3 rounded-xl bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-400 text-base";

function ResetPassword() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { token, user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  function validate() {
    const errs = {};
    if (!form.currentPassword.trim()) errs.currentPassword = 'Current password is required';
    if (!form.newPassword.trim()) {
      errs.newPassword = 'New password is required';
    } else if (form.newPassword.length < 6) {
      errs.newPassword = 'Password must be at least 6 characters';
    }
    if (!form.confirmPassword.trim()) {
      errs.confirmPassword = 'Please confirm your new password';
    } else if (form.newPassword !== form.confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setApiError('');
    setLoading(true);
    try {
      await axios.post(
        END_POINT_RESET_PASSWORD,
        { currentPassword: form.currentPassword, newPassword: form.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      updateUser({ ...user, firstTime: false });
      setTimeout(() => {
        if (!user?.onboardingComplete) {
          navigate('/onboarding');
        } else {
          navigate('/members');
        }
      }, 2000);
    } catch (err) {
      setApiError(err?.response?.data?.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  }

  if (success) {
    return (
      <section>
        <div className="home-section w-full" id="home">
          <Particle />
          <div className="home-content">
            <div className="flex justify-center px-4">
              <div className="w-full max-w-sm">
                <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">Password Updated!</h2>
                  <p className="text-gray-400 text-sm">Redirecting you now…</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="home-section w-full" id="home">
        <Particle />
        <div className="home-content">
          <div className="flex justify-center px-4">
            <div className="w-full max-w-sm">
              <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-6 pt-8 pb-2 text-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-1">Set new password</h2>
                  <p className="text-gray-400 text-sm">
                    Your admin has set a temporary password. Please set a new one before continuing.
                  </p>
                </div>

                {apiError && (
                  <div className="mx-6 mt-4 px-4 py-3 bg-red-900/40 border border-red-600 rounded-xl text-red-300 text-sm">
                    {apiError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Current password</label>
                    <div className="relative">
                      <input
                        type={showCurrent ? 'text' : 'password'}
                        name="currentPassword"
                        value={form.currentPassword}
                        onChange={handleChange}
                        placeholder="Enter current / temporary password"
                        className={inputClass + ' pr-8'}
                        autoComplete="current-password"
                      />
                      <button type="button" onClick={() => setShowCurrent(p => !p)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 bg-transparent focus:outline-none">
                        {showCurrent ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                      </button>
                    </div>
                    {errors.currentPassword && <p className="text-red-400 text-xs mt-1">{errors.currentPassword}</p>}
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm mb-1">New password</label>
                    <div className="relative">
                      <input
                        type={showNew ? 'text' : 'password'}
                        name="newPassword"
                        value={form.newPassword}
                        onChange={handleChange}
                        placeholder="Create a new password (min 6 chars)"
                        className={inputClass + ' pr-8'}
                        autoComplete="new-password"
                      />
                      <button type="button" onClick={() => setShowNew(p => !p)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 bg-transparent focus:outline-none">
                        {showNew ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                      </button>
                    </div>
                    {errors.newPassword && <p className="text-red-400 text-xs mt-1">{errors.newPassword}</p>}
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Confirm new password</label>
                    <div className="relative">
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        name="confirmPassword"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        placeholder="Repeat your new password"
                        className={inputClass + ' pr-8'}
                        autoComplete="new-password"
                      />
                      <button type="button" onClick={() => setShowConfirm(p => !p)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 bg-transparent focus:outline-none">
                        {showConfirm ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base transition disabled:opacity-60 mt-2"
                  >
                    {loading ? 'Updating…' : 'Update Password'}
                  </button>

                  <button
                    type="button"
                    onClick={logout}
                    className="w-full py-2 text-gray-500 text-sm hover:text-gray-300"
                  >
                    Sign out instead
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ResetPassword;
